import type { GitRepository } from "@webyonetim/contracts";
import { GitFork } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, SelectField } from "../../components/ui/Controls";
import { InlineResult, Skeleton } from "../../components/ui/Feedback";
import { apiErrorMessage } from "../../lib/auth";
import {
  isGithubRepositorySelection,
  sourcesApi,
  type SourceConnectionMutationResponse,
} from "../../lib/api";

interface CallbackInput {
  readonly state: string;
  readonly code: string;
  readonly installationId: string;
}

interface RepositorySelection {
  readonly token: string;
  readonly expiresAt: string;
  readonly repositories: readonly GitRepository[];
}

export function GithubCallbackPage() {
  const callback = useRef<CallbackInput | undefined>(readCallback(window.location.search));
  const started = useRef(false);
  const [selection, setSelection] = useState<RepositorySelection>();
  const [selectedRepositoryId, setSelectedRepositoryId] = useState("");
  const [connection, setConnection] = useState<SourceConnectionMutationResponse>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(callback.current !== undefined);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    window.history.replaceState(null, "", "/sources/github/callback");
    const input = callback.current;
    callback.current = undefined;
    if (input === undefined) {
      setError("GitHub dönüş bilgileri eksik veya geçersiz. Bağlantıyı projeden yeniden başlatın.");
      setBusy(false);
      return;
    }
    void sourcesApi
      .completeGithub(input)
      .then((response) => {
        if (isGithubRepositorySelection(response)) {
          setSelection({
            token: response.selectionToken,
            expiresAt: response.expiresAt,
            repositories: response.repositories,
          });
          setSelectedRepositoryId(response.repositories[0]?.providerRepositoryId ?? "");
        } else setConnection(response);
      })
      .catch((caught: unknown) => {
        setError(apiErrorMessage(caught));
      })
      .finally(() => {
        setBusy(false);
      });
  }, []);

  const selectRepository = async () => {
    if (selection === undefined || selectedRepositoryId === "") return;
    setBusy(true);
    setError(undefined);
    try {
      const response = await sourcesApi.selectGithub({
        selectionToken: selection.token,
        providerRepositoryId: selectedRepositoryId,
      });
      setSelection(undefined);
      setSelectedRepositoryId("");
      setConnection(response);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        description="Kurulum kimliği ve repository yetkisi sunucuda imzalı durumla doğrulanır."
        title="GitHub bağlantısı"
      />
      <section className="card form-stack">
        {busy && selection === undefined ? (
          <Skeleton label="GitHub bağlantısı doğrulanıyor" />
        ) : null}
        {error === undefined ? null : (
          <InlineResult tone="error">
            {error} <Link to="/projeler">Projelere dön</Link>
          </InlineResult>
        )}
        {selection === undefined ? null : (
          <>
            <div>
              <p className="eyebrow">Yetkili repository’ler</p>
              <h2>Bağlanacak repository’yi seçin</h2>
              <p>
                Bu seçim tek kullanımlık ve süreli kurulum iznine bağlıdır. Repository listesi
                GitHub kullanıcı yetkisinden doğrulandı.
              </p>
            </div>
            <SelectField
              help={`Seçim izni ${new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(selection.expiresAt))} tarihinde sona erer.`}
              label="GitHub repository"
              onChange={(event) => {
                setSelectedRepositoryId(event.currentTarget.value);
              }}
              value={selectedRepositoryId}
            >
              {selection.repositories.map((repository) => (
                <option
                  key={repository.providerRepositoryId}
                  value={repository.providerRepositoryId}
                >
                  {repository.fullName} · {repository.private ? "Private" : "Public"}
                </option>
              ))}
            </SelectField>
            <Button
              busy={busy}
              busyLabel="Repository bağlanıyor…"
              disabled={selectedRepositoryId === ""}
              onClick={() => {
                void selectRepository();
              }}
            >
              <GitFork aria-hidden="true" size={18} /> Repository’yi bağla
            </Button>
          </>
        )}
        {connection === undefined ? null : (
          <InlineResult requestId={connection.requestId}>
            <strong>{connection.connection.repository.fullName}</strong> güvenli GitHub App
            bağlantısıyla eklendi. <Link to="/projeler">Projeye dön</Link>
          </InlineResult>
        )}
      </section>
    </div>
  );
}

function readCallback(search: string): CallbackInput | undefined {
  const parameters = new URLSearchParams(search);
  const state = parameters.get("state");
  const code = parameters.get("code");
  const installationId = parameters.get("installation_id");
  if (
    state === null ||
    state.length < 80 ||
    state.length > 2_048 ||
    code === null ||
    !/^[A-Za-z0-9_-]{8,256}$/u.test(code) ||
    installationId === null ||
    !/^[0-9]{1,80}$/u.test(installationId)
  ) {
    return undefined;
  }
  return { state, code, installationId };
}
